import './MonitorModal.scss';

import classNames from 'classnames';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { EditMonitorFooter } from '@security/monitors/monitor-form/EditMonitorFooter';
import { isEqual } from 'lodash';
import { ModalProps } from 'antd';
import { MonitorFormTypes } from '@sonar-software/sonar-core';
import { SonarModal } from '@ui/modals';
import { SonarText } from '@ui/text';
import { useForm } from '@hooks';

import { defaultConfig, MenuOptions } from './monitor-form/MonitorMenu';
import { MonitorForm, MonitorFormRef } from './monitor-form/MonitorForm';
import React, { useCallback, useEffect, useRef, useState } from 'react';

export type CreateMonitorModalProps = ModalProps & {
	defaultMonitor?: MonitorFormTypes;
	menuOptions?: MenuOptions;
	monitor?: MonitorFormTypes;
	onCancel: () => void;
	onChange?: (monitor: MonitorFormTypes) => void;
	onSave: () => void;
};

export const CreateMonitorModal: React.FC<CreateMonitorModalProps> = ({
	defaultMonitor,
	menuOptions,
	monitor,
	onCancel,
	onChange,
	onSave,
	title,
	...rest
}) => {
	const { form } = useForm<MonitorFormTypes>({
		required: [ 'monitorConfig.monitorTypeId' ]
	});

	const ref = useRef<MonitorFormRef>(null);
	const [ currentMonitor, setCurrentMonitor ] = useState<MonitorFormTypes | undefined>(monitor);
	const [ isValid, setIsValid ] = useState<boolean>(ref.current?.isValid() ?? false);

	useEffect(() => {
		if (!isEqual(monitor, currentMonitor)) {
			setCurrentMonitor(monitor);
			form.setFieldsValue(monitor ?? defaultConfig);
			setIsValid(ref.current?.isValid() ?? Boolean(monitor?.templateId));
		}
	}, [ currentMonitor, form, monitor, ref ]);

	const handleClose = useCallback(() => {
		setCurrentMonitor(undefined);
		form.setFieldsValue(defaultConfig);
	}, [ form ]);

	const handleSave = useCallback((overwrite?: boolean) => {
		if (overwrite !== undefined) {
			ref.current?.saveTemplate(overwrite);
		} else if (currentMonitor?.templateId) {
			ref.current?.saveTemplate(false);
		} else {
			ref.current?.save();
		}

		onSave();
	}, [ currentMonitor, onSave, ref ]);

	const handleOnChange = useCallback((newMonitor: MonitorFormTypes) => {
		if (newMonitor && !isEqual(newMonitor, currentMonitor)) {
			setCurrentMonitor(newMonitor);

			if (onChange) {
				onChange(newMonitor);
			}

		}

		setIsValid(ref.current?.isValid() ?? false);

	}, [ currentMonitor, onChange, setCurrentMonitor ]);

	return (
		<SonarModal
			afterClose={ handleClose }
			centered
			className='monitor-modal flex-inputs'
			destroyOnHidden
			footer={
				<EditMonitorFooter
					disableSave={ !isValid }
					monitor={ currentMonitor }
					onCancel={ onCancel }
					onSave={ handleSave } />
			}
			maskClosable={ false }
			onCancel={ onCancel }
			title={
				<SonarText className={ classNames('h3', 'monitor-modal-header') }>
					{ title ?? COPY_SPECIFIC.CREATE_NEW_MONITOR }
				</SonarText>
			}
			{ ... rest }>
			<MonitorForm
				defaultMonitor={ defaultMonitor }
				form={ form }
				menuOptions={ menuOptions }
				monitor={ currentMonitor }
				onChange={ handleOnChange }
				ref={ ref }
				showRecommendedActionTemplates />
		</SonarModal>);
};
